import type { CSSProperties } from 'react';

export interface CompanyBrandProfile {
  accent: string;
  background: string;
  border: string;
  buttonBackground: string;
  buttonBorder: string;
  buttonHoverBackground: string;
  buttonSelectedBackground: string;
  buttonSelectedBorder: string;
  buttonSelectedText: string;
  buttonText: string;
  companyName: string;
  customerName: string;
  domain: string;
  faviconUrl?: string;
  fontFamily?: string;
  initials: string;
  primary: string;
  primaryContrast: string;
  primaryDark: string;
  primarySoft: string;
  surface: string;
  technicianName: string;
  text: string;
  textMuted: string;
}

export const COMPANY_BRAND_FONT_OPTIONS = [
  {
    value: 'Inter',
    stack: '"Inter Variable", Inter, "Public Sans Variable", sans-serif',
  },
  {
    value: 'Public Sans',
    stack: '"Public Sans Variable", "Public Sans", Inter, sans-serif',
  },
  {
    value: 'DM Sans',
    stack: '"DM Sans Variable", "DM Sans", Inter, sans-serif',
  },
  {
    value: 'Nunito Sans',
    stack: '"Nunito Sans Variable", "Nunito Sans", Inter, sans-serif',
  },
  {
    value: 'Barlow',
    stack: 'Barlow, "Public Sans Variable", Arial, sans-serif',
  },
] as const;

export function resolveCompanyBrandFontStack(fontFamily?: string | null): string {
  const normalized = fontFamily?.trim();
  return (
    COMPANY_BRAND_FONT_OPTIONS.find((option) => option.value === normalized)?.stack ??
    COMPANY_BRAND_FONT_OPTIONS[1].stack
  );
}

export const brooksBrandProfile: CompanyBrandProfile = {
  accent: 'var(--color-primary-500)',
  background: 'var(--color-gray-50)',
  border: 'var(--color-gray-200)',
  buttonBackground: 'var(--color-base-white)',
  buttonBorder: 'var(--color-gray-200)',
  buttonHoverBackground: 'var(--color-gray-50)',
  buttonSelectedBackground: 'var(--color-success-50)',
  buttonSelectedBorder: 'var(--color-success-600)',
  buttonSelectedText: 'var(--color-success-700)',
  buttonText: 'var(--color-gray-900)',
  companyName: 'Brooks Pest Control',
  customerName: 'Austin',
  domain: 'brooks.svcpro.io',
  initials: 'BP',
  primary: 'var(--color-success-600)',
  primaryContrast: 'var(--color-base-white)',
  primaryDark: 'var(--color-success-700)',
  primarySoft: 'var(--color-success-50)',
  surface: 'var(--color-base-white)',
  technicianName: 'Daniel',
  text: 'var(--color-gray-900)',
  textMuted: 'var(--color-gray-600)',
};

export const triguardBrandProfile: CompanyBrandProfile = {
  accent: '#879783',
  background: '#F6F6F2',
  border: '#DDE2DA',
  buttonBackground: '#FFFFFF',
  buttonBorder: '#DDE2DA',
  buttonHoverBackground: '#F0F3EE',
  buttonSelectedBackground: '#EEF2EC',
  buttonSelectedBorder: '#73856F',
  buttonSelectedText: '#263329',
  buttonText: '#171A18',
  companyName: 'TriGuard Pest Control',
  customerName: 'Austin',
  domain: 'triguard.svcpro.io',
  fontFamily: 'Public Sans',
  initials: 'TG',
  primary: '#7F917B',
  primaryContrast: '#FFFFFF',
  primaryDark: '#263329',
  primarySoft: '#EEF2EC',
  surface: '#FFFFFF',
  technicianName: 'Daniel',
  text: '#171A18',
  textMuted: '#667067',
};

export function getCompanyBrandCssVars(brand: CompanyBrandProfile): CSSProperties {
  const fontFamily = resolveCompanyBrandFontStack(brand.fontFamily);

  return {
    '--font-family': fontFamily,
    '--typography-font-family-primary': fontFamily,
    '--p-font-family': fontFamily,
    '--cx-brand-accent': brand.accent,
    '--cx-brand-bg': brand.background,
    '--cx-brand-border': brand.border,
    '--cx-brand-button-bg': brand.buttonBackground,
    '--cx-brand-button-border': brand.buttonBorder,
    '--cx-brand-button-hover-bg': brand.buttonHoverBackground,
    '--cx-brand-button-selected-bg': brand.buttonSelectedBackground,
    '--cx-brand-button-selected-border': brand.buttonSelectedBorder,
    '--cx-brand-button-selected-text': brand.buttonSelectedText,
    '--cx-brand-button-text': brand.buttonText,
    '--cx-brand-font-family': fontFamily,
    '--cx-brand-primary': brand.primary,
    '--cx-brand-primary-contrast': brand.primaryContrast,
    '--cx-brand-primary-dark': brand.primaryDark,
    '--cx-brand-primary-soft': brand.primarySoft,
    '--cx-brand-surface': brand.surface,
    '--cx-brand-text': brand.text,
    '--cx-brand-text-muted': brand.textMuted,
  } as CSSProperties;
}
